import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { ProviderPairConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/CoordinatedCareConversation'

import { getAnchorId } from './conversationGrouping'
import { fallbackProviderPairProfile } from './providerPairCareHistory'
import { buildProviderPairThread } from './providerPairUtils'

export type ClientInboxSummary = {
  threadCount: number
  providerPairThreadCount: number
  unreadCount: number
  lastMessageTime: Date | null
}

type InboxThread = {
  clientId: string
  conversationId: string
  unreadCount: number
  lastMessageSentAt: string | null | undefined
  isProviderPair: boolean
}

export const summarizeClientInbox = (
  providerClientConversations: ConversationObject[],
  providerPairConversations: ProviderPairConversationObject[],
): Map<string, ClientInboxSummary> => {
  const threads: InboxThread[] = [
    ...providerClientConversations.map((conversation) => ({
      clientId: getAnchorId(conversation),
      conversationId: conversation.conversation_id,
      unreadCount: conversation.conversation_attributes?.unread_provider_messages_count ?? 0,
      lastMessageSentAt: conversation.conversation_last_message_sent_datetime,
      isProviderPair: false,
    })),
    ...providerPairConversations.flatMap((conversation) => {
      // The inbox only counts threads, so it uses the messenger's no-profile fallback instead of a
      // roster lookup; this keeps the same threads the messenger shows, including historical providers.
      const thread = buildProviderPairThread(conversation, fallbackProviderPairProfile)
      if (!thread) {
        return []
      }
      return [
        {
          clientId: conversation.anchor_patient_lyra_id,
          conversationId: conversation.external_conversation_id,
          unreadCount: thread.unreadCount,
          lastMessageSentAt: conversation.conversation_last_message_sent_datetime,
          isProviderPair: true,
        },
      ]
    }),
  ]

  const summaries = new Map<string, ClientInboxSummary>()
  const seenConversationIds = new Set<string>()
  for (const { clientId, conversationId, unreadCount, lastMessageSentAt, isProviderPair } of threads) {
    if (!clientId || !conversationId || seenConversationIds.has(conversationId)) {
      continue
    }
    seenConversationIds.add(conversationId)
    let summary = summaries.get(clientId)
    if (!summary) {
      summary = { threadCount: 0, providerPairThreadCount: 0, unreadCount: 0, lastMessageTime: null }
      summaries.set(clientId, summary)
    }
    summary.threadCount += 1
    summary.providerPairThreadCount += Number(isProviderPair)
    summary.unreadCount += unreadCount
    const messageTime = lastMessageSentAt ? new Date(lastMessageSentAt) : null
    if (
      messageTime &&
      Number.isFinite(messageTime.getTime()) &&
      (!summary.lastMessageTime || messageTime > summary.lastMessageTime)
    ) {
      summary.lastMessageTime = messageTime
    }
  }

  return summaries
}
